import type { Pipeline } from "@/schema"
import { Edge } from "@xyflow/react"

import { getActionDefinition } from "../definitions"
import { resolveActionProvidedSubjects } from "../definitions/actions/provided-subjects"
import { getTriggerProvidedSubjects } from "../definitions/triggers"
import {
  ActionNodeData,
  AppNode,
  AutomationSubjectType,
  BranchChildNodeData,
  NodeKey,
  ResultSchema,
  TriggerConfig,
} from "../types"
import { getActionDisplayName } from "./action-display-name"
import { getUpstreamNodeIds } from "./upstream-node-ids"

/**
 * Information about an upstream action node with a result schema.
 * Used for generating step result placeholders.
 */
export interface UpstreamActionInfo {
  guid: string
  label: string
  nodeKey: NodeKey
  resultSchema: ResultSchema
  isAiAction: boolean
}

/**
 * Computes the available subjects at a specific node in the automation.
 * Walks backwards from the given node to find all subjects provided by
 * upstream triggers and actions.
 *
 * @param nodes - All nodes in the automation
 * @param edges - All edges in the automation
 * @param nodeId - The node to compute available subjects for
 * @returns Set of subject types available at this node
 */
export function computeAvailableSubjects(
  nodes: AppNode[],
  edges: Edge[],
  nodeId: string,
  trigger?: TriggerConfig,
  pipelines?: Pipeline[]
): Set<AutomationSubjectType> {
  const subjects = new Set<AutomationSubjectType>()

  // Build a map from node ID to incoming edges for faster traversal
  const incomingEdges = new Map<string, Edge[]>()
  for (const edge of edges) {
    const existing = incomingEdges.get(edge.target) ?? []
    existing.push(edge)
    incomingEdges.set(edge.target, existing)
  }

  // Build a map of nodes by ID for quick lookup
  const nodeMap = new Map<string, AppNode>()
  for (const node of nodes) {
    nodeMap.set(node.id, node)
  }

  // Walk backwards from the target node, collecting subjects
  const visited = new Set<string>()
  const queue: string[] = [nodeId]

  while (queue.length > 0) {
    const currentId = queue.shift()!
    if (visited.has(currentId)) continue
    visited.add(currentId)

    const currentNode = nodeMap.get(currentId)
    if (!currentNode) continue

    // Collect subjects from this node
    collectSubjectsFromNode(currentNode, subjects, trigger, pipelines)

    // Find all parent nodes (nodes that have edges pointing to this one)
    const incoming = incomingEdges.get(currentId) ?? []
    for (const edge of incoming) {
      if (!visited.has(edge.source)) {
        queue.push(edge.source)
      }
    }

    // Special case: branch child nodes need to trace back through their parent branch action
    if (currentNode.type === "branchChild") {
      const parentId = (currentNode.data as BranchChildNodeData).parentId
      if (parentId && !visited.has(parentId)) {
        queue.push(parentId)
      }
    }
  }

  return subjects
}

/**
 * Collects subjects provided by a single node into the subjects set.
 */
function collectSubjectsFromNode(
  node: AppNode,
  subjects: Set<AutomationSubjectType>,
  trigger?: TriggerConfig,
  pipelines?: Pipeline[]
): void {
  const nodeKey = node.data.nodeKey

  if (node.type === "trigger") {
    for (const subject of getTriggerProvidedSubjects(nodeKey, trigger, pipelines)) subjects.add(subject)
  } else if (node.type === "action") {
    const actionDef = getActionDefinition(nodeKey)
    if (actionDef) {
      for (const subject of resolveActionProvidedSubjects(actionDef, (node.data as ActionNodeData).fields)) {
        subjects.add(subject)
      }
    }
  }
  // branchChild and note nodes don't provide subjects
}

/**
 * Checks if an action's required subjects are satisfied by the available subjects.
 *
 * @param actionKey - The action node key to check
 * @param availableSubjects - Set of subjects available in scope
 * @returns true if all required subjects are available, false otherwise
 */
export function actionSubjectsAreSatisfied(actionKey: string, availableSubjects: Set<AutomationSubjectType>): boolean {
  const actionDef = getActionDefinition(actionKey as Parameters<typeof getActionDefinition>[0])
  if (!actionDef) return true // Unknown action, don't gate it

  // Check requiresSubjects (AND logic - all must be present)
  const requiredSubjects = actionDef.requiresSubjects
  if (requiredSubjects && requiredSubjects.length > 0) {
    if (!requiredSubjects.every((subject) => availableSubjects.has(subject))) {
      return false
    }
  }

  // Check requiresAnySubject (OR logic - at least one must be present)
  const requiresAnySubject = actionDef.requiresAnySubject
  if (requiresAnySubject && requiresAnySubject.length > 0) {
    if (!requiresAnySubject.some((subject) => availableSubjects.has(subject))) {
      return false
    }
  }

  return true
}

/**
 * Checks if an action is available for the selected trigger.
 */
export function actionTriggerIsSatisfied(actionKey: string, trigger?: TriggerConfig, pipelines?: Pipeline[]): boolean {
  const actionDef = getActionDefinition(actionKey as Parameters<typeof getActionDefinition>[0])
  if (!actionDef) return true

  const subjects = getTriggerProvidedSubjects(trigger?.trigger_key, trigger, pipelines)
  if (actionDef.excludedTriggerSubjects?.some((subject) => subjects.includes(subject))) return false
  if (!actionDef.requiresTriggerKeys?.length) return true

  return trigger?.trigger_key !== undefined && actionDef.requiresTriggerKeys.includes(trigger.trigger_key)
}

/**
 * Gets the list of missing subjects for an action given the available subjects.
 *
 * @param actionKey - The action node key to check
 * @param availableSubjects - Set of subjects available in scope
 * @param fields - Current action field values used to validate subject-backed selections
 * @returns Array of missing subject types, empty if all are satisfied
 */
export function getMissingSubjects(
  actionKey: string,
  availableSubjects: Set<AutomationSubjectType>,
  fields?: ActionNodeData["fields"]
): AutomationSubjectType[] {
  const actionDef = getActionDefinition(actionKey as Parameters<typeof getActionDefinition>[0])
  if (!actionDef) return []

  const missing: AutomationSubjectType[] = []

  // Check requiresSubjects (AND logic - report all missing)
  if (actionDef.requiresSubjects) {
    missing.push(...actionDef.requiresSubjects.filter((subject) => !availableSubjects.has(subject)))
  }

  // Check requiresAnySubject (OR logic - report all if none present)
  if (actionDef.requiresAnySubject && actionDef.requiresAnySubject.length > 0) {
    const hasAny = actionDef.requiresAnySubject.some((subject) => availableSubjects.has(subject))
    if (!hasAny) {
      missing.push(...actionDef.requiresAnySubject)
    }
  }

  for (const field of actionDef.fields.filter((candidate) => candidate.subjectOptions)) {
    const selectedSubject = fields?.[field.key]
    if (typeof selectedSubject === "string" && !availableSubjects.has(selectedSubject as AutomationSubjectType)) {
      missing.push(selectedSubject as AutomationSubjectType)
    }
  }

  return [...new Set(missing)]
}

/**
 * Checks if a specific action key exists in the upstream path of a given node.
 * Walks backwards from the given node to find if any upstream action has the target key.
 *
 * @param nodes - All nodes in the automation
 * @param edges - All edges in the automation
 * @param nodeId - The node to start searching from
 * @param targetActionKey - The action key to look for
 * @returns true if the action key exists upstream, false otherwise
 */
export function hasActionKeyInUpstreamPath(
  nodes: AppNode[],
  edges: Edge[],
  nodeId: string,
  targetActionKey: string
): boolean {
  const incomingEdges = new Map<string, Edge[]>()
  for (const edge of edges) {
    const existing = incomingEdges.get(edge.target) ?? []
    existing.push(edge)
    incomingEdges.set(edge.target, existing)
  }

  const nodeMap = new Map<string, AppNode>()
  for (const node of nodes) {
    nodeMap.set(node.id, node)
  }

  const visited = new Set<string>()
  const queue: string[] = [nodeId]

  while (queue.length > 0) {
    const currentId = queue.shift()!
    if (visited.has(currentId)) continue
    visited.add(currentId)

    const currentNode = nodeMap.get(currentId)
    if (!currentNode) continue

    if (currentNode.type === "action" && currentNode.data.nodeKey === targetActionKey) {
      return true
    }

    const incoming = incomingEdges.get(currentId) ?? []
    for (const edge of incoming) {
      if (!visited.has(edge.source)) {
        queue.push(edge.source)
      }
    }

    if (currentNode.type === "branchChild") {
      const parentId = (currentNode.data as BranchChildNodeData).parentId
      if (parentId && !visited.has(parentId)) {
        queue.push(parentId)
      }
    }
  }

  return false
}

/**
 * Checks if a specific action key exists in the downstream path of a given node.
 * Walks forwards from the given node through all branches to find if any
 * downstream action has the target key.
 *
 * @param nodes - All nodes in the automation
 * @param edges - All edges in the automation
 * @param nodeId - The node to start searching from
 * @param targetActionKey - The action key to look for
 * @returns true if the action key exists downstream, false otherwise
 */
export function hasActionKeyInDownstreamPath(
  nodes: AppNode[],
  edges: Edge[],
  nodeId: string,
  targetActionKey: string
): boolean {
  const outgoingEdges = new Map<string, Edge[]>()
  for (const edge of edges) {
    const existing = outgoingEdges.get(edge.source) ?? []
    existing.push(edge)
    outgoingEdges.set(edge.source, existing)
  }

  const nodeMap = new Map<string, AppNode>()
  for (const node of nodes) {
    nodeMap.set(node.id, node)
  }

  // Build a map of branch parent ID to their branchChild nodes
  const branchChildrenMap = new Map<string, AppNode[]>()
  for (const node of nodes) {
    if (node.type === "branchChild") {
      const parentId = (node.data as BranchChildNodeData).parentId
      if (parentId) {
        const existing = branchChildrenMap.get(parentId) ?? []
        existing.push(node)
        branchChildrenMap.set(parentId, existing)
      }
    }
  }

  const visited = new Set<string>()
  const queue: string[] = [nodeId]

  while (queue.length > 0) {
    const currentId = queue.shift()!
    if (visited.has(currentId)) continue
    visited.add(currentId)

    const currentNode = nodeMap.get(currentId)
    if (!currentNode) continue

    if (currentNode.type === "action" && currentNode.data.nodeKey === targetActionKey) {
      return true
    }

    // Follow outgoing edges to children
    const outgoing = outgoingEdges.get(currentId) ?? []
    for (const edge of outgoing) {
      if (!visited.has(edge.target)) {
        queue.push(edge.target)
      }
    }

    // For branch actions, also traverse into all branch children
    if (currentNode.type === "action") {
      const branchChildren = branchChildrenMap.get(currentId) ?? []
      for (const child of branchChildren) {
        if (!visited.has(child.id)) {
          queue.push(child.id)
        }
      }
    }
  }

  return false
}

/**
 * Checks if a specific action key exists anywhere in the path of a given node
 * (both upstream and downstream). This ensures that only one instance of the
 * action can exist in any single path through the automation.
 *
 * @param nodes - All nodes in the automation
 * @param edges - All edges in the automation
 * @param nodeId - The node to check from
 * @param targetActionKey - The action key to look for
 * @returns true if the action key exists in the path (upstream or downstream), false otherwise
 */
export function hasActionKeyInPath(nodes: AppNode[], edges: Edge[], nodeId: string, targetActionKey: string): boolean {
  return (
    hasActionKeyInUpstreamPath(nodes, edges, nodeId, targetActionKey) ||
    hasActionKeyInDownstreamPath(nodes, edges, nodeId, targetActionKey)
  )
}

/**
 * Finds the trigger node in the automation.
 */
export function findTriggerNode(nodes: AppNode[]): AppNode | undefined {
  return nodes.find((node) => node.type === "trigger")
}

/**
 * Gets the subjects provided by the trigger in an automation.
 */
export function getTriggerSubjects(nodes: AppNode[]): Set<AutomationSubjectType> {
  const trigger = findTriggerNode(nodes)
  if (!trigger) return new Set()

  const subjects = new Set<AutomationSubjectType>()
  collectSubjectsFromNode(trigger, subjects)
  return subjects
}

/**
 * Computes all upstream action nodes that have a result schema.
 * Walks backwards from the given node to find all actions that produce results.
 *
 * @param nodes - All nodes in the automation
 * @param edges - All edges in the automation
 * @param nodeId - The node to compute upstream actions for
 * @returns Array of upstream action info with their result schemas
 */
export function computeUpstreamActionsWithResults(
  nodes: AppNode[],
  edges: Edge[],
  nodeId: string
): UpstreamActionInfo[] {
  const upstreamActions: UpstreamActionInfo[] = []
  const nodeMap = new Map(nodes.map((node) => [node.id, node]))

  for (const currentId of getUpstreamNodeIds(nodes, edges, nodeId)) {
    const currentNode = nodeMap.get(currentId)
    if (!currentNode) continue

    if (currentNode.type === "action" && currentNode.data.nodeKey) {
      const actionDef = getActionDefinition(currentNode.data.nodeKey)
      if (actionDef?.resultSchema) {
        const actionData = currentNode.data as ActionNodeData
        upstreamActions.push({
          guid: actionData.guid,
          label: getActionDisplayName(actionData),
          nodeKey: currentNode.data.nodeKey,
          resultSchema: actionDef.resultSchema,
          isAiAction: actionDef.category === "ai",
        })
      }
    }
  }

  return upstreamActions
}
